import { OverrideThemeProvider } from '@cogneed/design-system';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FlagProvider } from '@unleash/proxy-client-react';
import ReactDOM from 'react-dom/client';
import { HashRouter } from 'react-router-dom';
import AppInstallContextProvider from './App/Contexts/AppInstallContext/AppInstallContextProvider';
// import AudioDeviceStateProvider from './App/Contexts/AppStateContexts/AudioDeviceStateProvider';
import SwContextProvider from './App/Contexts/SwContext/SwContextProvider';
import ConnectWebsocketProvider from './connectWebsocket/ConnectWebsocketProvider';
import { applyChannelRedirect } from './App/Contexts/SwContext/versionPolling';
import { IsWindowVisibleProvider } from './App/LiveContext/Hooks/useIsWindowVisible';
import AuthenticatedApp from './AuthenticatedApp';
import OfflineScreenWrapper from './Components/OfflineScreenWrapper';
import './awsRum';
import config from './config';
import './datadog';
import './dualChannel/dualChannelApis';
import './i18n';
import './index.css';
import reportWebVitals from './reportWebVitals';
import liveThemeOverride from './themeOverride';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },

  queryCache: new QueryCache({
    onError: (error, query) => {
      console.error(`An error occurred trying to fetch for key "${JSON.stringify(query.queryKey)}"`, error);
    },
  }),
});

(async () => {
  if (applyChannelRedirect()) return;

  // When the external browser receives the Cognito callback for a dual-canal flow,
  // show a redirect page that bridges the code back to Electron via deep link.
  const urlParams = new URL(window.location.href).searchParams;
  const code = urlParams.get('code');
  const state = urlParams.get('state');
  if (!window.authAPI && code && state?.startsWith('dc.')) {
    const { RedirectProtocol } = config.Authentication;
    const deepLink = `${RedirectProtocol}://auth-callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
    const { default: AuthCallbackRedirectPage } = await import('./AuthCallbackRedirectPage');
    const React = await import('react');
    root.render(React.createElement(AuthCallbackRedirectPage, { deepLink }));
    return;
  }

  if (window.authAPI) {
    // Dual-canal context: finalize any pending OAuth code before React mounts,
    // then configure Amplify with the desktop-specific settings.
    const { finalizeHostedUiSignInFromUrl } = await import('./dualChannel/hostedUi');
    await finalizeHostedUiSignInFromUrl();
    const { default: configureDualCanalAmplify } = await import('./amplify/dualCanalConfig');
    configureDualCanalAmplify();
  } else {
    // Standard web context: configure Amplify via aws-exports.
    await import('./amplify/aws-exports');
  }

  root.render(
    <AppInstallContextProvider>
      <SwContextProvider>
        <OverrideThemeProvider override={liveThemeOverride}>
          <OfflineScreenWrapper>
            <FlagProvider config={config.Unleash}>
              <QueryClientProvider client={queryClient}>
                <IsWindowVisibleProvider>
                  <HashRouter>
                    <ConnectWebsocketProvider>
                      <AuthenticatedApp />
                    </ConnectWebsocketProvider>
                  </HashRouter>
                </IsWindowVisibleProvider>
              </QueryClientProvider>
            </FlagProvider>
          </OfflineScreenWrapper>
        </OverrideThemeProvider>
      </SwContextProvider>
    </AppInstallContextProvider>,
  );

  reportWebVitals();
})();
